import { mapState, mapActions } from 'pinia'
import { useUserStore } from '@/stores/user'
import { binddevice } from '@/api/activity'
import { getMiniProgramConfig } from '@/config/miniProgram'

/**
 * 设备扫码绑定 / 心电开始测量（与 unbind 旧版逻辑一致，供新版监测页复用）
 * 页面可覆盖 afterDeviceBindSuccess、getDeviceBindPagePath
 */
export default {
  data() {
    return {
      /** 使用步骤弹窗本地图片 */
      imgGuideStep01: require('@/views/binding/img/step_01.jpg'),
      imgGuideStep02: require('@/views/binding/img/step_02.jpg'),
      imgGuideStep03: require('@/views/binding/img/step_03.jpg'),
      imgGuideStep04: require('@/views/binding/img/step_04.jpg'),
      /** 当前正在绑定的设备类型 */
      currentBindingDeviceType: null,
      /** 心电使用步骤弹窗 */
      guideDialog: false,
      /** 当前步骤索引 */
      currentStepIndex: 0,
      /** 智能手表绑定提醒弹框 */
      watchBindingReminderDialog: false,
      /** 倒计时秒数 */
      watchBindingCountdown: 5,
      /** 倒计时定时器 */
      watchBindingCountdownTimer: null
    }
  },
  computed: {
    ...mapState(useUserStore, ['userInfo'])
  },
  beforeDestroy() {
    if (this.watchBindingCountdownTimer) {
      clearInterval(this.watchBindingCountdownTimer)
      this.watchBindingCountdownTimer = null
    }
  },
  methods: {
    ...mapActions(useUserStore, ['setUserInfo']),

    /**
     * 绑定成功后钩子（页面可覆盖，如刷新设备列表）
     * @returns {void}
     */
    afterDeviceBindSuccess() {},

    /**
     * 小程序扫码返回后清理 URL 使用的 path
     * @returns {string}
     */
    getDeviceBindPagePath() {
      return (this.$route && this.$route.path) || '/unbind'
    },

    /**
     * 处理小程序扫码绑定返回：刷新并提示
     * @returns {void}
     */
    handleMiniProgramBindReturn() {
      if (!(this.$env && this.$env.inMiniProgram)) return
      const q = (this.$route && this.$route.query) ? this.$route.query : {}
      if (typeof q.bindSuccess === 'undefined') return

      this.afterDeviceBindSuccess()

      const ok = String(q.bindOk || '') === '1'
      const msg = q.bindMsg
        ? decodeURIComponent(String(q.bindMsg))
        : (ok ? '绑定成功' : '绑定失败')
      if (ok) {
        this.$message({ type: 'success', message: msg })
      } else {
        this.$message({ type: 'error', message: msg })
      }

      try {
        this.$router.replace({ path: this.getDeviceBindPagePath(), query: {} })
      } catch (e) {}
    },

    /**
     * 绑定设备（原生扫码回调）
     * @param {string} str 二维码扫码数据
     * @returns {void}
     */
    bindDevice(str) {
      console.log(str, '二维码扫码数据')
      console.log('当前绑定的设备类型:', this.currentBindingDeviceType)

      let deviceTypeCode = ''
      if (this.currentBindingDeviceType === 'watch') {
        deviceTypeCode = '10010002'
      } else if (this.currentBindingDeviceType === 'bloodPressure') {
        deviceTypeCode = '10030001'
      } else if (this.currentBindingDeviceType === 'ecg') {
        deviceTypeCode = '10040001'
      } else {
        console.error('未知的设备类型:', this.currentBindingDeviceType)
        this.$message.error('设备类型错误')
        return
      }

      const bindParams = {
        deviceCode: '',
        deviceType: deviceTypeCode,
        bindStatus: '02',
        jsonBrs: str
      }

      binddevice(bindParams).then((res) => {
        console.log('绑定设备响应:', res)
        if (res.code == 1000) {
          this.$message({
            type: 'success',
            message: '绑定成功!'
          })

          this.setUserInfo({
            id: this.userInfo.id,
            name: this.userInfo.name,
            userCode: this.userInfo.userCode,
            idNo: this.userInfo.idNo,
            avatar: this.userInfo.avatar,
            age: this.userInfo.age,
            sex: this.userInfo.sex,
            userPhone: this.userInfo.userPhone,
            token: this.userInfo.token,
            refreshToken: this.userInfo.refreshToken,
            isBinding: true
          })

          this.afterDeviceBindSuccess()
          this.currentBindingDeviceType = null
        } else {
          this.$message.error(res.message || '绑定失败')
        }
      }).catch((error) => {
        console.error('绑定设备失败:', error)
        this.$message.error('绑定失败，请重试')
      })
    },

    /**
     * 启动扫码绑定 / 心电开始测量
     * @param {'watch'|'bloodPressure'|'ecg'} deviceType 设备类型
     * @returns {void}
     */
    startScanBinding(deviceType) {
      this.currentBindingDeviceType = deviceType

      if (deviceType === 'ecg') {
        this.showGuideDialog()
        return
      }

      if (deviceType === 'watch') {
        this.showWatchBindingReminder()
        return
      }

      this.proceedWithScanCode()
    },

    /**
     * 显示智能手表绑定提醒弹框
     * @returns {void}
     */
    showWatchBindingReminder() {
      this.watchBindingCountdown = 5
      this.watchBindingReminderDialog = true

      if (this.watchBindingCountdownTimer) {
        clearInterval(this.watchBindingCountdownTimer)
      }

      this.watchBindingCountdownTimer = setInterval(() => {
        this.watchBindingCountdown--
        if (this.watchBindingCountdown <= 0) {
          clearInterval(this.watchBindingCountdownTimer)
          this.watchBindingCountdownTimer = null
        }
      }, 1000)
    },

    /**
     * 关闭智能手表绑定提醒弹框并继续扫码
     * @returns {void}
     */
    closeWatchBindingReminder() {
      if (this.watchBindingCountdownTimer) {
        clearInterval(this.watchBindingCountdownTimer)
        this.watchBindingCountdownTimer = null
      }
      this.watchBindingReminderDialog = false
      this.proceedWithScanCode()
    },

    /**
     * 继续扫码流程（小程序 / App 原生）
     * @returns {void}
     */
    proceedWithScanCode() {
      if (this.$env && this.$env.inMiniProgram) {
        try {
          if (typeof wx !== 'undefined' && wx.miniProgram && typeof wx.miniProgram.navigateTo === 'function') {
            const token = localStorage.getItem('token') || ''
            const apiBase = (typeof window !== 'undefined' && window.SEVSRS) ? window.SEVSRS : ''
            const baseH5 = (window.location.origin || '') + (window.location.pathname || '')
            const pagePath = this.getDeviceBindPagePath()
            const hashPart = window.location.hash || ('#' + pagePath)
            const params = [
              'baseH5=' + encodeURIComponent(baseH5),
              'hashPart=' + encodeURIComponent(hashPart),
              'token=' + encodeURIComponent(token),
              'deviceType=' + encodeURIComponent(this.currentBindingDeviceType || ''),
              'apiBase=' + encodeURIComponent(apiBase)
            ].filter(p => p.length > 0).join('&')
            wx.miniProgram.navigateTo({
              url: '/pages/scan/index?' + params
            })
          } else {
            this.$message.error('请在微信小程序中打开')
          }
        } catch (e) {
          console.warn('小程序扫码跳转失败:', e)
          this.$message.error('扫码打开失败，请重试')
        }
        return
      }

      if (this.$env.inDjxApp) {
        if (this.$env.inAndroid) {
          JSInterface.actionScanCode()
        } else if (this.$env.inIos) {
          window.webkit.messageHandlers.actionScanCode.postMessage(null)
        }
      } else {
        this.$message.error('请在APP中使用扫码功能')
      }
    },

    /**
     * 显示心电使用步骤弹窗
     * @returns {void}
     */
    showGuideDialog() {
      this.currentStepIndex = 0
      this.guideDialog = true
      this.$nextTick(() => {
        if (this.$refs.guideSwipe) {
          this.$refs.guideSwipe.swipeTo(0)
        }
      })
    },

    /**
     * 跳过当前步骤，直接到第四步
     * @returns {void}
     */
    skipGuide() {
      this.$nextTick(() => {
        if (this.$refs.guideSwipe) {
          this.$refs.guideSwipe.swipeTo(3)
        }
      })
    },

    /**
     * 跳转到下一步
     * @returns {void}
     */
    goToNextStep() {
      if (this.currentStepIndex < 3) {
        this.$nextTick(() => {
          if (this.$refs.guideSwipe) {
            this.$refs.guideSwipe.swipeTo(this.currentStepIndex + 1)
          }
        })
      }
    },

    /**
     * 步骤切换事件
     * @param {number} index 当前步骤索引
     * @returns {void}
     */
    onStepChange(index) {
      this.currentStepIndex = index
    },

    /**
     * 连接记录仪（关闭引导并跳转小程序）
     * @returns {void}
     */
    connectDevice() {
      this.guideDialog = false
      this.jumpToWeChatMiniProgram()
    },

    /**
     * 关闭使用步骤弹窗
     * @returns {void}
     */
    closeGuideDialog() {
      this.guideDialog = false
    },

    /**
     * 跳转到微信小程序（心电蓝牙连接）
     * @returns {Promise<void>}
     */
    async jumpToWeChatMiniProgram() {
      if (this.$env && this.$env.inMiniProgram && typeof wx !== 'undefined' && wx.miniProgram && typeof wx.miniProgram.navigateTo === 'function') {
        try {
          const userPhone = (this.userInfo && this.userInfo.userPhone)
            ? encodeURIComponent(this.userInfo.userPhone)
            : ''
          wx.miniProgram.navigateTo({
            url: '/pages/bridge/index?action=openEcg&userPhone=' + userPhone
          })
        } catch (e) {
          console.warn('小程序桥接页跳转失败', e)
          this.$message.error('打开失败，请重试')
        }
        return
      }

      const miniProgramConfig = getMiniProgramConfig('ecg')
      const userAgent = navigator.userAgent.toLowerCase()
      const isWeChat = userAgent.includes('micromessenger')

      if (isWeChat) {
        this.launchInWeChat(miniProgramConfig)
      } else {
        this.launchWithPlainTextScheme(miniProgramConfig)
      }
    },

    /**
     * 在微信内使用开放标签跳转
     * @param {Object} config 小程序配置
     * @returns {void}
     */
    launchInWeChat(config) {
      if (typeof wx === 'undefined') {
        console.error('微信JS-SDK未加载')
        this.fallbackLaunch(config)
        return
      }

      const userPhone = this.userInfo.userPhone || ''
      const pathWithParams = this.buildPathWithUserPhone(config.path, userPhone)

      const wxTag = document.createElement('wx-open-launch-weapp')
      wxTag.setAttribute('username', config.originalId)
      wxTag.setAttribute('path', pathWithParams)

      const button = document.createElement('button')
      button.textContent = '正在跳转到小程序...'
      button.style.cssText = `
        width: 100%;
        height: 40px;
        background: #07c160;
        color: white;
        border: none;
        border-radius: 4px;
        font-size: 16px;
        cursor: pointer;
      `

      wxTag.appendChild(button)
      document.body.appendChild(wxTag)

      wxTag.addEventListener('launch', () => {
        this.$message.success('正在跳转到小程序...')
      })

      wxTag.addEventListener('error', (e) => {
        console.error('小程序启动失败:', e.detail)
        this.fallbackLaunch(config)
      })

      setTimeout(() => button.click(), 100)
      setTimeout(() => {
        if (document.body.contains(wxTag)) {
          document.body.removeChild(wxTag)
        }
      }, 5000)
    },

    /**
     * 构建带用户手机号的页面路径
     * @param {string} basePath 基础页面路径
     * @param {string} userPhone 用户手机号
     * @returns {string}
     */
    buildPathWithUserPhone(basePath, userPhone) {
      if (!userPhone) return basePath
      const params = new URLSearchParams({ userPhone })
      const queryString = params.toString()
      return queryString ? `${basePath}?${queryString}` : basePath
    },

    /**
     * 使用明文 URL Scheme 跳转（微信外）
     * @param {Object} config 小程序配置
     * @returns {void}
     */
    launchWithPlainTextScheme(config) {
      const plainTextScheme = 'weixin://dl/business/?appid=wx5d984a32674e2a19&path=pages/index/index&query=userPhone%3D' + this.userInfo.userPhone
      this.launchWithAndroid(plainTextScheme)
    },

    /**
     * iOS 系统跳转方式
     * @param {string} scheme URL Scheme
     * @returns {void}
     */
    launchWithIOS(scheme) {
      try {
        const link = document.createElement('a')
        link.href = scheme
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        this.$message.success('正在打开微信小程序...')
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link)
          }
        }, 1000)
        setTimeout(() => {
          const iframe = document.createElement('iframe')
          iframe.style.display = 'none'
          iframe.src = scheme
          document.body.appendChild(iframe)
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe)
            }
          }, 2000)
        }, 500)
      } catch (error) {
        console.error('iOS跳转失败:', error)
        this.$message.error('打开微信小程序失败，请确保已安装微信')
      }
    },

    /**
     * Android / 通用跳转方式
     * @param {string} scheme URL Scheme
     * @returns {void}
     */
    launchWithAndroid(scheme) {
      try {
        window.location.href = scheme
        this.$message.success('正在打开微信小程序...')
      } catch (error) {
        console.error('Android跳转失败:', error)
        this.$message.error('打开微信小程序失败，请确保已安装微信')
      }
    },

    /**
     * 备选方案：引导用户手动打开小程序
     * @param {Object} config 小程序配置
     * @returns {void}
     */
    fallbackLaunch(config) {
      this.$alert(`无法直接唤起小程序，请手动打开微信搜索小程序：${config.name}`, '提示', {
        confirmButtonText: '确定',
        type: 'warning'
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/deviceBindMeasureMixin.js